/*
 * Copyright (c) CIRROM 2026.
 */

import React, { useMemo, useRef } from "react";
import {
  CircularProgress,
  Box,
  CardHeader,
  Typography,
  Button,
} from "@mui/material";
import { ContactProblemSummary } from "../../api/dashboards";
import { MiraCard, MiraCardContent } from "../mira/components/common/Card";
import styled from "@emotion/styled";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS } from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import "chart.js/auto";

// chart.js/auto registers the core; the zoom plugin must be registered explicitly.
ChartJS.register(zoomPlugin);

const ChartContainer = styled.div`
  height: 420px;
`;

const HowToRead = styled.div`
  border-left: 4px solid rgb(208, 56, 44);
  background: rgba(208, 56, 44, 0.06);
  padding: 12px 16px;
  margin-top: 16px;
  border-radius: 4px;
`;

// "Governed" = recalculated against the deduplicated contact set: contacts
// flagged as duplicates (is_duplicate) are removed entirely, and each remaining
// contact's problems are reduced by its problem_count_80.
const BLUE = "rgb(45, 95, 214)";
const RED = "rgb(208, 56, 44)";
const BLUE_FILL = "rgba(45, 95, 214, 0.12)";
const RED_FILL = "rgba(208, 56, 44, 0.1)";

type Granularity = "month" | "day";

interface Bucket {
  key: string;
  rawContacts: number;
  governedContacts: number;
  rawProblems: number;
  governedProblems: number;
}

const bucketKey = (iso: string, granularity: Granularity): string => {
  const d = new Date(iso);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  if (granularity === "month") return `${year}-${month}`;
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatLabel = (key: string, granularity: Granularity): string => {
  const d = new Date(granularity === "month" ? `${key}-01` : key);
  return granularity === "month"
    ? d.toLocaleDateString("en-US", { month: "short", year: "2-digit" })
    : d.toLocaleDateString("en-US", { day: "numeric", month: "short" });
};

const buildBuckets = (
  data: ContactProblemSummary[],
  granularity: Granularity
): Bucket[] => {
  const map = new Map<string, Bucket>();
  for (const row of data) {
    if (!row.contact_created_date) continue;
    const key = bucketKey(row.contact_created_date, granularity);
    let bucket = map.get(key);
    if (!bucket) {
      bucket = {
        key,
        rawContacts: 0,
        governedContacts: 0,
        rawProblems: 0,
        governedProblems: 0,
      };
      map.set(key, bucket);
    }
    bucket.rawContacts += 1;
    bucket.rawProblems += row.problem_count;
    if (!row.is_duplicate) {
      bucket.governedContacts += 1;
      bucket.governedProblems += row.problem_count - row.problem_count_80;
    }
  }
  return Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
};

const cumulative = (values: number[]): number[] => {
  let running = 0;
  return values.map((v) => Math.round((running += v)));
};

const percentile = (values: number[], p: number): number => {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const idx = Math.min(
    sorted.length - 1,
    Math.floor((p / 100) * sorted.length)
  );
  return sorted[idx];
};

const baseOptions = (yTitle: string, maxTicks?: number) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index" as const, intersect: false },
  elements: { point: { radius: 0 } },
  plugins: { legend: { position: "top" as const } },
  scales: {
    x: {
      grid: { color: "rgba(0,0,0,0.0375)" },
      ticks: maxTicks ? { maxTicksLimit: maxTicks } : {},
    },
    y: {
      beginAtZero: true,
      grid: { color: "rgba(0,0,0,0.0375)" },
      title: { display: true, text: yTitle },
    },
  },
});

interface ChartProps {
  contactProblemSummary: ContactProblemSummary[];
  isLoading: boolean;
}

const Loading: React.FC = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="200px"
  >
    <CircularProgress />
  </Box>
);

export const CumulativeContactProblemChart: React.FC<ChartProps> = ({
  contactProblemSummary,
  isLoading,
}) => {
  const chartData = useMemo(() => {
    const buckets = buildBuckets(contactProblemSummary, "month");
    return {
      labels: buckets.map((b) => formatLabel(b.key, "month")),
      datasets: [
        {
          label: "Contacts — governed from day one",
          data: cumulative(buckets.map((b) => b.governedContacts)),
          borderColor: BLUE,
          borderDash: [6, 4],
          fill: false,
          tension: 0.3,
        },
        {
          label: "All contacts (ungoverned)",
          data: cumulative(buckets.map((b) => b.rawContacts)),
          borderColor: BLUE,
          backgroundColor: BLUE_FILL,
          fill: 0,
          tension: 0.3,
        },
        {
          label: "Problems — governed from day one",
          data: cumulative(buckets.map((b) => b.governedProblems)),
          borderColor: RED,
          borderDash: [6, 4],
          fill: false,
          tension: 0.3,
        },
        {
          label: "All problems (ungoverned)",
          data: cumulative(buckets.map((b) => b.rawProblems)),
          borderColor: RED,
          backgroundColor: RED_FILL,
          fill: 2,
          tension: 0.3,
        },
      ],
    };
  }, [contactProblemSummary]);

  return (
    <MiraCard>
      <CardHeader
        title="Cumulative Contact Volume & Problems Over Time"
        subheader="Total contacts and data quality problems accumulated since earliest records. The shaded gap between the solid and dashed lines shows the compounding effect of ungoverned growth."
      />
      <MiraCardContent>
        {isLoading ? (
          <Loading />
        ) : (
          <>
            <ChartContainer>
              <Line
                data={chartData}
                options={baseOptions("Cumulative total")}
              />
            </ChartContainer>
            <HowToRead>
              <Typography variant="body2">
                <strong>How to read this:</strong> Solid lines = ungoverned
                (reality). Dashed lines = what the picture looks like with
                Cirrom&apos;s daily governance applied from day one. The wider
                the shaded gap, the more value governance has delivered.
              </Typography>
            </HowToRead>
          </>
        )}
      </MiraCardContent>
    </MiraCard>
  );
};

export const DailyContactProblemChart: React.FC<ChartProps> = ({
  contactProblemSummary,
  isLoading,
}) => {
  const chartRef = useRef<ChartJS<"line">>(null);

  const { chartData, yMax, yLimitMax } = useMemo(() => {
    const buckets = buildBuckets(contactProblemSummary, "day");
    const chartData = {
      labels: buckets.map((b) => formatLabel(b.key, "day")),
      datasets: [
        {
          label: "New contacts (after daily governance)",
          data: buckets.map((b) => b.governedContacts),
          borderColor: BLUE,
          borderDash: [6, 4],
          fill: false,
          tension: 0.3,
        },
        {
          label: "New contacts (raw)",
          data: buckets.map((b) => b.rawContacts),
          borderColor: BLUE,
          backgroundColor: BLUE_FILL,
          fill: 0,
          tension: 0.3,
        },
        {
          label: "Problems introduced (after governance)",
          data: buckets.map((b) => b.governedProblems),
          borderColor: RED,
          borderDash: [6, 4],
          fill: false,
          tension: 0.3,
        },
        {
          label: "Problems introduced (raw)",
          data: buckets.map((b) => b.rawProblems),
          borderColor: RED,
          backgroundColor: RED_FILL,
          fill: 2,
          tension: 0.3,
        },
      ],
    };

    // A single outlier day (e.g. a bulk import) can be orders of magnitude
    // larger than typical days and would flatten the whole chart. Default the
    // y-axis to ~the 95th percentile so the everyday signal is readable; the
    // true max stays reachable by zooming/panning out (see limits below).
    const allValues = buckets.flatMap((b) => [
      b.rawContacts,
      b.governedContacts,
      b.rawProblems,
      b.governedProblems,
    ]);
    const dataMax = allValues.reduce((m, v) => (v > m ? v : m), 0);
    const p95 = percentile(allValues, 95);
    const yMax = Math.max(10, Math.ceil((p95 * 1.25) / 10) * 10);
    const yLimitMax = Math.max(dataMax, yMax);

    return { chartData, yMax, yLimitMax };
  }, [contactProblemSummary]);

  const options = useMemo(() => {
    const base = baseOptions("Count per day", 12);
    return {
      ...base,
      scales: {
        ...base.scales,
        y: { ...base.scales.y, max: yMax },
      },
      plugins: {
        ...base.plugins,
        zoom: {
          zoom: {
            wheel: { enabled: true },
            mode: "xy" as const,
            // Keep the y-axis anchored to its baseline: pinning min to 0 means
            // zooming only pulls the top down (so spikes shrink into view)
            // instead of recentring on the cursor and floating above the axis.
            onZoom: ({ chart }: { chart: ChartJS }) => {
              const y = chart.scales.y;
              if (y.min !== 0) {
                chart.zoomScale("y", { min: 0, max: y.max }, "none");
              }
            },
          },
          // Pan horizontally only, so the view can never drift off the baseline.
          pan: { enabled: true, mode: "x" as const },
          // Allow zooming out to the true peak, but never below the baseline.
          limits: { y: { min: 0, max: yLimitMax } },
        },
      },
    };
  }, [yMax, yLimitMax]);

  return (
    <MiraCard>
      <CardHeader
        title="Daily New Contacts & Problems Introduced"
        subheader="New contacts created each day, and the data quality problems they arrive with. Governed figures show what daily Cirrom processing leaves in the system."
        action={
          <Button size="small" onClick={() => chartRef.current?.resetZoom()}>
            Reset zoom
          </Button>
        }
      />
      <MiraCardContent>
        {isLoading ? (
          <Loading />
        ) : (
          <>
            <ChartContainer>
              <Line ref={chartRef} data={chartData} options={options} />
            </ChartContainer>
            <Typography variant="caption" color="textSecondary">
              The y-axis is scaled to everyday volumes — scroll to zoom out and
              reveal outlier spikes, drag to pan, Reset zoom to restore.
            </Typography>
            <HowToRead>
              <Typography variant="body2">
                <strong>How to read this:</strong> Every day, new contacts
                arrive carrying problems. Without daily governance those
                problems sit unresolved. Cirrom processes them overnight — so by
                the next morning, duplicates are flagged, missing data is
                identified, and the CRM reflects reality.
              </Typography>
            </HowToRead>
          </>
        )}
      </MiraCardContent>
    </MiraCard>
  );
};
